import React, { useCallback, useState, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";

import { Formik } from "formik";
import * as Yup from "yup";

import { SaveButton, ResetButton } from "../../components";
import { SelectTitle, Text, Address, PhoneFormikField } from "../Forms";
import * as select from "../../reducers";
import * as actionsSettings from "../../actions/settings";
import * as actionsModals from "../../actions/modals";
import { displayAddress } from "../../helpers/Address";

const yupString = Yup.string().typeError("Please enter a value");

const validationSchema = Yup.object().shape({
  honorific: yupString.required("Please enter a title"),
  honorific_other: yupString.test({
    name: "isOther",
    message: "Please enter a title",
    test: (value, { parent }) => {
      if (parent.honorific !== "Other") {
        return true;
      }
      return !!value?.length;
    },
  }),
  first_name: yupString
    .max(40, "Please enter a name of 40 characters or fewer")
    .matches(/^((?!\b(and)\b).)*$/gi, "Please donate as an individual")
    .required("Please enter a first name"),
  last_name: yupString
    .max(40, "Please enter a name of 40 characters or fewer")
    .matches(/^((?!\b(and)\b).)*$/gi, "Please donate as an individual")
    .required("Please enter a last name"),
  phone: Yup.string()
    .nullable()
    .test({
      name: "phone",
      message: "Please enter at least one phone number",
      test: (value, { parent }) => {
        if (parent.mobile) return true;
        return !!value?.length;
      },
    }),
  mobile: Yup.string()
    .nullable()
    .test({
      name: "mobile",
      message: "Please enter at least one phone number",
      test: (value, { parent }) => {
        if (parent.phone) return true;
        return !!value?.length;
      },
    }),
  email: yupString
    .email("Please enter a valid email address")
    .required("Please enter an email address"),
  address: Yup.object()
    .shape({})
    .typeError("Please enter an address")
    .required("Please enter an address"),
  country: yupString.required("Please enter a country"),
  zip: yupString.required("Please enter a postcode / zipcode"),
  address_line_1: yupString.required("Please enter an address"),
  state: yupString.required("Please enter a state"),
  city: yupString.required("Please enter a city"),
});

function SettingsPersonalForm({
  settingsPersonalSaving,
  settingsPersonalError,
  settingsPersonalSuccess,
  settingsPersonalDone,
  setModalPasswordResetOpen,
  settingsIsStaff,
  settingsIsSSOUser,
}) {
  const dispatch = useDispatch();
  const [enterAddressManually, setEnterAddressManually] = useState(false);

  const initialValues = useSelector((state) => state.settings.details) || {
    mobile: "",
    phone: "",
  };

  const isOrganisation = useMemo(
    () => !!initialValues?.organisation_name,
    [initialValues]
  );

  const googleAddressClassName =
    enterAddressManually || settingsIsStaff ? "hidden" : "padded-row";
  const manualAddressClassName =
    enterAddressManually || settingsIsStaff ? "padded-row" : "hidden";

  const toggleAddressEntry = useCallback(() => {
    setEnterAddressManually((prev) => !prev);
    // setTimeout(() => {
    //   addressEl.focus();
    // }, 0);
  }, []);

  const handleManualChange = useCallback((e, values, setValues) => {
    const obj = {
      ...values,
      [e.target.name]: e.target.value,
    };
    obj.address = displayAddress(obj);
    setValues(obj);
  }, []);

  const handleAutocompleteChange = useCallback((address, setValues) => {
    const newValues = { ...address };
    delete newValues.display;
    setValues((prev) => ({ ...prev, ...newValues, address }));
  }, []);

  // Define the state of the save button
  let buttonType = "";
  if (settingsPersonalSaving) buttonType = "saving";
  if (settingsPersonalError) buttonType = "error";
  if (settingsPersonalSuccess) buttonType = "success";
  if (settingsPersonalDone) buttonType = "";

  const onSubmit = useCallback(
    async (values, { setErrors }) => {
      const honorific =
        typeof values.honorific === "string"
          ? values.honorific
          : values.honorific.label;
      await actionsSettings.saveSettingsPersonal(
        { ...values, honorific },
        dispatch,
        setErrors
      );
    },
    [dispatch]
  );

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ handleSubmit, resetForm, values, setValues }) => {
        return (
          <form
            id="settingsPersonalForm"
            className="form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <fieldset>
              <legend className="settings__heading">
                {isOrganisation ? "Organisation" : "Personal"}
              </legend>
              {isOrganisation && (
                <div className="flex-row form__row">
                  <div className="col-12">
                    <Text
                      label="Organisation name (for donations on behalf of)"
                      name="organisation_name"
                      className="flex-row__stretch form__email-address"
                      disabled
                      autoComplete="off"
                    />
                    <>
                      <br />
                      <p className="form__alert">
                        To update your organisation name, please contact us at{" "}
                        <a href="mailto:support@afes.org.au">
                          support@afes.org.au
                        </a>
                        , <a href="tel:1800989028">1800 989 028</a> or{" "}
                        <a href="tel:+61296970313">+61 2 9697 0313</a>
                      </p>
                    </>
                  </div>
                </div>
              )}
              {settingsIsStaff && (
                <p className="form__alert">
                  Please contact the national office to change personal details.
                </p>
              )}
              <div className="flex-row form__row">
                <div>
                  <SelectTitle
                    name="honorific"
                    className="flex-row__auto form__title-name"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                    otherDisabled={settingsIsStaff || settingsIsSSOUser}
                  />
                </div>
              </div>
              <div className="flex-row form__row">
                <div className="col-6">
                  <Text
                    label="First Name"
                    name="first_name"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                    autoComplete="off"
                  />
                </div>
                <div className="col-6">
                  <Text
                    label="Last Name"
                    name="last_name"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="flex-row form__row">
                <div className="col-12">
                  <Text
                    label="Email Address"
                    name="email"
                    className="flex-row__stretch form__email-address"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="flex-row form__row">
                <div className="col-6">
                  <PhoneFormikField
                    label="Mobile Number"
                    name="mobile"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                  />
                </div>
                <div className="col-6">
                  <PhoneFormikField
                    label="Phone Number"
                    name="phone"
                    disabled={settingsIsStaff || settingsIsSSOUser}
                  />
                  <div className="form_note">Please include area code</div>
                </div>
              </div>
              {!settingsIsSSOUser && !settingsIsStaff && (
                <div className="flex-row form__row">
                  <div className="flex-row__stretch change-password-link">
                    <button
                      className="fs--12 btn btn__link--text"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setModalPasswordResetOpen();
                      }}
                    >
                      Change Password
                    </button>
                    &nbsp;|&nbsp;
                    <a href="/account/security-questions" className="fs--12">
                      Security Question
                    </a>
                  </div>
                </div>
              )}
              <div className={manualAddressClassName}>
                <div className="flex-row form__row">
                  <div className="col-6">
                    <Text
                      label="Address Line 1"
                      name="address_line_1"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff || settingsIsSSOUser}
                    />
                  </div>
                  <div className="col-6">
                    <Text
                      label="Address Line 2"
                      name="address_line_2"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff}
                    />
                  </div>
                </div>
                <div className="flex-row form__row">
                  <div className="col-6">
                    <Text
                      label="City"
                      name="city"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff}
                    />
                  </div>
                  <div className="col-6">
                    <Text
                      label="State"
                      name="state"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff}
                    />
                  </div>
                </div>
                <div className="flex-row form__row">
                  <div className="col-6">
                    <Text
                      label="Postcode/Zip Code"
                      name="zip"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff}
                    />
                  </div>
                  <div className="col-6">
                    <Text
                      label="Country"
                      name="country"
                      onChange={(e) => handleManualChange(e, values, setValues)}
                      className="flex-row__stretch"
                      autoComplete="off"
                      disabled={settingsIsStaff}
                    />
                  </div>
                </div>
                <div className="form_row form_note form__row__mb2x align_right">
                  <button
                    type="button"
                    className="fs--12 btn btn__link--text"
                    onClick={toggleAddressEntry}
                  >
                    Search for your address.
                  </button>
                </div>
              </div>
              {!settingsIsStaff && (
                <>
                  <div className={googleAddressClassName}>
                    <div className="flex-row form__row form__row__mb0">
                      <div className="col-12">
                        <Address
                          label="Postal Address"
                          name="address"
                          onSelectSuggest={(e) =>
                            handleAutocompleteChange(e, setValues)
                          }
                          className="flex-row__stretch"
                          textNoResults={null}
                        />
                      </div>
                    </div>
                    <div className="form_row form_note form__row__mb2x align_right">
                      Having trouble finding your address?{" "}
                      <button
                        type="button"
                        className="fs--12 btn btn__link--text"
                        onClick={toggleAddressEntry}
                      >
                        You can enter your address manually.
                      </button>
                    </div>
                  </div>
                  <div className="flex-row form__finalise-changes">
                    <ResetButton
                      action="reset"
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Reset
                    </ResetButton>
                    <SaveButton
                      variant={buttonType}
                      onClick={handleSubmit}
                      onTryAgain={handleSubmit}
                    >
                      Save Changes
                    </SaveButton>
                  </div>
                </>
              )}
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    settingsPersonalSaving: select.getSettingsPersonalSaving(state),
    settingsPersonalError: select.getSettingsPersonalError(state),
    settingsPersonalSuccess: select.getSettingsPersonalSuccess(state),
    settingsPersonalDone: select.getSettingsPersonalDone(state),
    settingsIsStaff: select.getSettingsIsStaff(state),
    settingsIsSSOUser: select.getIsSSOAccount(state),
  };
};

export default connect(mapStateToProps, {
  ...actionsSettings,
  ...actionsModals,
})(SettingsPersonalForm);
